import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import { Form, HStack, LoadingPlaceholder, theme, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';
import { formatBirthdayForSubmit, getMonthNames } from '@order/utils';

import {
  RequestAccountDeletionModal,
  RequestAccountDeletionTrigger,
  useRequestAccountDeletionModal,
} from '../../components';
import { useCustomer } from '../../context/Customer';
import { US_PHONE_NUMBER_MAX_LENGTH } from './constants';
import { useProfileForm } from './useProfileForm';

import type {
  CustomerProfileFieldsQuery,
  useUpdateCustomerProfileMutation,
} from './GraphQL/Profile.generated';
import type { ProfileFormData } from './useProfileForm';

// ────────────────────────────────────────────────────────────────────────────────

export const ProfileForm = (props: ProfileFormProps) => {
  const { customer, submitForm, onFormReset, isLoading, isSubmitting } = props;
  const { formatMessage } = useIntl();
  const { signOut } = useCustomer();
  const { match } = useResponsive();

  const birthdayContainerA11ySelector = useA11ySelector('account.profile.birthday-container'); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const isZipCodeFieldEnabled = useFeatureFlag('CELS-3186-enable-zip-code-field', {
    listenForChanges: true,
  });

  // ────────────────────────────────────────────────────────────────────

  const {
    isShowingRequestAccountDeletionModal,
    showRequestAccountDeletionModal,
    hideRequestAccountDeletionModal,
  } = useRequestAccountDeletionModal();

  // ────────────────────────────────────────────────────────────────────

  const { form, isSubmitAllowed, resetForm } = useProfileForm({
    customer,
    hasZipCode: isZipCodeFieldEnabled,
  });

  const { control, handleSubmit, formState, resetField } = form;
  const { errors, isSubmitSuccessful, dirtyFields } = formState;
  const monthNames = getMonthNames().map((key) => formatMessage(profileFormMonthMessageMap[key]));
  const { track } = useTelemetry();

  // ─── HELPERS ────────────────────────────────────────────────────────

  const onReset = useCallback(() => {
    resetForm();
    onFormReset?.();
  }, [onFormReset, resetForm]);

  const onSubmit = useCallback(
    async (formData: ProfileFormData) => {
      const { firstName, lastName, phoneNumber, confirmPassword, birthday, zipCode } = formData;
      const changedFields = Object.keys(dirtyFields).filter((key) => key !== 'confirmPassword');

      track('account.profile_saved', { changedFields });

      await submitForm({
        input: {
          firstName,
          lastName,
          contactNumber: phoneNumber,
          birthday: formatBirthdayForSubmit(birthday),
          zipCode,

          // ─── Conditional fields ──────────────────────

          ...(confirmPassword ? { password: confirmPassword } : {}),
        },
      });
    },
    [dirtyFields, submitForm, track],
  );

  // ─── FORM EFFECTS ────────────────────────────────────────────────────────

  useEffect(() => {
    resetForm();
  }, [customer, resetForm]);

  useEffect(() => {
    if (isSubmitSuccessful && !isSubmitting) {
      resetField('confirmPassword');
      resetField('password');
    }
  }, [isSubmitSuccessful, isSubmitting, resetField]);

  // ────────────────────────────────────────────────────────────────────────────────

  if (isLoading) return <AccountScreenLoading />;

  return (
    <>
      <Form.Container
        contentStyle={match([styles.containerContentXS, styles.containerContentSM])}
        removeSpacing={true}
      >
        {/* ========= = first name ========== */}
        <Form.Group>
          <Form.TextField
            name="firstName"
            control={control}
            rules={{ required: true }}
            testID="account.profile.firstName"
            label={formatMessage(messages.formFirstName)}
            hideSubmitButton
            autoComplete="name"
            notice={errors.firstName?.message}
            disabled={isSubmitting}
          />
        </Form.Group>

        {/* ========= = last name ========== */}
        <Form.Group>
          <Form.TextField
            name="lastName"
            control={control}
            rules={{ required: true }}
            testID="account.profile.lastName"
            label={formatMessage(messages.formLastName)}
            hideSubmitButton
            autoComplete="name"
            notice={errors.lastName?.message}
            disabled={isSubmitting}
          />
        </Form.Group>

        {/* ========= = email ========== */}
        <Form.Group>
          <Form.TextField
            name="email"
            control={control}
            rules={{ required: true }}
            keyboardType="email-address"
            testID="account.profile.email"
            label={formatMessage(messages.formEmail)}
            hideSubmitButton
            autoComplete="email"
            notice={errors.email?.message}
            disabled={true}
            accessibilityState={{ disabled: true }}
          />
        </Form.Group>

        {/* ========= = phone number ========== */}
        <Form.Group>
          <Form.TextFieldPhoneNumber
            name="phoneNumber"
            control={control}
            rules={{ required: true }}
            testID="account.profile.phoneNumber"
            label={formatMessage(messages.formPhoneNumber)}
            maxLength={US_PHONE_NUMBER_MAX_LENGTH}
            hideSubmitButton
            notice={errors.phoneNumber?.message}
            disabled={isSubmitting}
          />
        </Form.Group>

        {/* ========= = zip code ========== */}
        {isZipCodeFieldEnabled ? (
          <Form.Group>
            <Form.TextFieldZipCode
              name="zipCode"
              control={control}
              rules={{ required: true }}
              testID="account.profile.zipCode"
              label={formatMessage(messages.formZipCode)}
              maxLength={8}
              hideSubmitButton
              notice={errors.zipCode?.message}
              disabled={isSubmitting}
            />
          </Form.Group>
        ) : null}

        {/* ========= = birthday ========== */}
        <Form.Group {...birthdayContainerA11ySelector.props}>
          <Form.BirthdayPicker
            name="birthday"
            control={control}
            label={formatMessage(messages.formBirthday)}
            accessibilityLabel={formatMessage(messages.formBirthday)}
            monthLabel={formatMessage(messages.profileFormMonth)}
            dayLabel={formatMessage(messages.profileFormDay)}
            helperText={formatMessage(messages.joinBirthdayHelperText)}
            monthNames={monthNames}
          />
        </Form.Group>

        {/* ========= = ========== ========== */}
        <HStack itemsPerRow={2} style={styles.buttonContainer}>
          <Form.SubmitButton
            palette="secondary"
            onPress={onReset}
            disabled={isSubmitting}
            testID="account.profile.cancel-button"
            style={styles.button}
          >
            {formatMessage(messages.accountProfileCancel)}
          </Form.SubmitButton>

          <Form.SubmitButton
            testID="account.profile.save-button"
            disabled={!isSubmitAllowed}
            isLoading={isSubmitting}
            onPress={handleSubmit(onSubmit)}
            accessibilityState={{ disabled: !isSubmitAllowed }}
            style={styles.button}
          >
            {formatMessage(messages.accountProfileSave)}
          </Form.SubmitButton>
        </HStack>

        <RequestAccountDeletionTrigger
          isDisabled={isSubmitting}
          onRequestAccountDeletion={showRequestAccountDeletionModal}
        />
      </Form.Container>

      <RequestAccountDeletionModal
        visible={isShowingRequestAccountDeletionModal}
        onSuccess={signOut}
        onClose={hideRequestAccountDeletionModal}
      />
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerContentXS: {
    paddingTop: theme.spacing['2'],
    paddingBottom: theme.spacing['6'],
  },
  containerContentSM: {
    paddingVertical: theme.spacing['8'],
    paddingHorizontal: theme.spacing['8'],
    borderColor: theme.colors.GRAY,
    borderRadius: theme.radius.large,
    borderWidth: 1,
  },
  buttonContainer: { paddingTop: theme.spacing['4'] },
  button: { paddingTop: 0 },
});

// ────────────────────────────────────────────────────────────────────────────────

type ProfileFormProps = Readonly<{
  customer: CustomerProfileFieldsQuery['currentCustomer'];
  isLoading: boolean;
  isSubmitting: boolean;
  onFormReset?: () => void;
  submitForm: UpdateCallback;
}>;

type UpdateCallback = ReturnType<typeof useUpdateCustomerProfileMutation>[1];

// ────────────────────────────────────────────────────────────────────────────────

const AccountScreenLoading = () => (
  <Form.Container>
    <LoadingPlaceholder rows={8} rowHeight={[100, 60, 70, 70, 70, 70, 70, 80]} palette="cream" />
  </Form.Container>
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  formFirstName: {
    defaultMessage: 'First name',
    description: 'Form | First Name',
  },
  formLastName: {
    defaultMessage: 'Last name',
    description: 'Form | Last Name',
  },
  formEmail: {
    defaultMessage: 'Email',
    description: 'Form | Email',
  },
  formPhoneNumber: {
    defaultMessage: 'Phone number',
    description: 'Form | Phone Number',
  },
  formZipCode: {
    defaultMessage: 'Zip code',
    description: 'Form | Zip Code',
  },
  formBirthday: {
    defaultMessage: 'Birthday',
    description: 'Form | Birthday',
  },
  profileFormMonth: {
    defaultMessage: 'Month',
    description: 'Profile Form | Month',
  },
  profileFormDay: {
    defaultMessage: 'Day',
    description: 'Profile Form | Day',
  },
  joinBirthdayHelperText: {
    defaultMessage: '(optional) So we can celebrate your big day',
    description: 'Join | Birthday Helper Text',
  },
  accountProfileCancel: {
    defaultMessage: 'Cancel',
    description: 'Account | Profile | Cancel',
  },
  accountProfileSave: {
    defaultMessage: 'Save',
    description: 'Account | Profile | Save',
  },
});

const monthMessages = defineMessages({
  profileFormMonthJanuary: {
    defaultMessage: 'January',
    description: 'Profile Form | Month Option | January',
  },
  profileFormMonthFebruary: {
    defaultMessage: 'February',
    description: 'Profile Form | Month Option | February',
  },
  profileFormMonthMarch: {
    defaultMessage: 'March',
    description: 'Profile Form | Month Option | March',
  },
  profileFormMonthApril: {
    defaultMessage: 'April',
    description: 'Profile Form | Month Option | April',
  },
  profileFormMonthMay: {
    defaultMessage: 'May',
    description: 'Profile Form | Month Option | May',
  },
  profileFormMonthJune: {
    defaultMessage: 'June',
    description: 'Profile Form | Month Option | June',
  },
  profileFormMonthJuly: {
    defaultMessage: 'July',
    description: 'Profile Form | Month Option | July',
  },
  profileFormMonthAugust: {
    defaultMessage: 'August',
    description: 'Profile Form | Month Option | August',
  },
  profileFormMonthSeptember: {
    defaultMessage: 'September',
    description: 'Profile Form | Month Option | September',
  },
  profileFormMonthOctober: {
    defaultMessage: 'October',
    description: 'Profile Form | Month Option | October',
  },
  profileFormMonthNovember: {
    defaultMessage: 'November',
    description: 'Profile Form | Month Option | November',
  },
  profileFormMonthDecember: {
    defaultMessage: 'December',
    description: 'Profile Form | Month Option | December',
  },
});

const profileFormMonthMessageMap = {
  'general.month.january': monthMessages.profileFormMonthJanuary,
  'general.month.february': monthMessages.profileFormMonthFebruary,
  'general.month.march': monthMessages.profileFormMonthMarch,
  'general.month.april': monthMessages.profileFormMonthApril,
  'general.month.may': monthMessages.profileFormMonthMay,
  'general.month.june': monthMessages.profileFormMonthJune,
  'general.month.july': monthMessages.profileFormMonthJuly,
  'general.month.august': monthMessages.profileFormMonthAugust,
  'general.month.september': monthMessages.profileFormMonthSeptember,
  'general.month.october': monthMessages.profileFormMonthOctober,
  'general.month.november': monthMessages.profileFormMonthNovember,
  'general.month.december': monthMessages.profileFormMonthDecember,
} as const;
